/**
 * Every change in the admin panel is confirmed with a fresh 2FA code (step-up, owner's choice).
 * This dialog shows what will happen, any extra fields (reason, typed confirmation…), the code
 * box, and runs the action. Errors stay in the dialog so nothing is lost.
 */
import { Button, CodeInput, Field, Modal, Notice, toast } from "@custos/ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { errorMessage } from "../lib/errors";

export function StepUpModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  danger = false,
  canConfirm = true,
  onConfirm,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** Extra validation for the fields in `children`. */
  canConfirm?: boolean;
  onConfirm: (totpCode: string) => Promise<unknown>;
  onDone?: () => void;
}) {
  const [code, setCode] = useState("");
  const action = useMutation({
    mutationFn: (totpCode: string) => onConfirm(totpCode),
    onSuccess: () => {
      onOpenChange(false);
      toast("Done");
      onDone?.();
    },
  });

  useEffect(() => {
    if (open) {
      setCode("");
      action.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.length === 6 && canConfirm) action.mutate(code);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} {...(description ? { description } : {})}>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {children}
        <Field label="Your 2FA code" hint="Each change needs a fresh code from your authenticator app.">
          {(p) => <CodeInput {...p} value={code} onChange={setCode} />}
        </Field>
        {action.error && <Notice tone="danger">{errorMessage(action.error)}</Notice>}
        <Button
          type="submit"
          variant={danger ? "danger" : "primary"}
          block
          loading={action.isPending}
          disabled={code.length !== 6 || !canConfirm}
        >
          {confirmLabel}
        </Button>
      </form>
    </Modal>
  );
}
